import React from 'react';
import Icon from '../../../components/AppIcon';

const GenerationStatusCard = ({ status, progress, message, onCancel }) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'processing':
        return {
          icon: 'Loader2',
          iconColor: 'var(--color-primary)',
          bgColor: 'bg-primary/10',
          borderColor: 'border-primary/20',
          title: 'Generating Article',
          showProgress: true,
          showCancel: true
        };
      case 'success':
        return {
          icon: 'CheckCircle',
          iconColor: 'var(--color-success)',
          bgColor: 'bg-success/10',
          borderColor: 'border-success/20',
          title: 'Generation Complete',
          showProgress: false,
          showCancel: false
        };
      case 'error':
        return {
          icon: 'AlertCircle',
          iconColor: 'var(--color-error)',
          bgColor: 'bg-error/10',
          borderColor: 'border-error/20',
          title: 'Generation Failed',
          showProgress: false,
          showCancel: false
        };
      case 'limit_reached':
        return {
          icon: 'Crown',
          iconColor: 'var(--color-warning)',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning/20',
          title: 'Usage Limit Reached',
          showProgress: false,
          showCancel: false
        };
      default:
        return {
          icon: 'Info',
          iconColor: 'var(--color-muted-foreground)',
          bgColor: 'bg-muted',
          borderColor: 'border-border',
          title: 'Ready',
          showProgress: false,
          showCancel: false
        };
    }
  };

  const config = getStatusConfig();

  if (!status || status === 'idle') {
    return null;
  }

  return (
    <div className={`${config?.bgColor} ${config?.borderColor} border rounded-lg p-4 mb-6`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={status === 'processing' ? 'animate-spin' : ''}>
            <Icon 
              name={config?.icon} 
              size={20} 
              color={config?.iconColor} 
            />
          </div>
          <div>
            <h3 className="font-medium text-foreground">
              {config?.title}
            </h3>
            {message && (
              <p className="text-sm text-muted-foreground mt-1">
                {message}
              </p>
            )}
          </div>
        </div>

        {config?.showCancel && onCancel && (
          <button
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground transition-colors duration-200"
            title="Cancel generation"
          >
            <Icon name="X" size={16} />
          </button>
        )}
      </div>
      {config?.showProgress && progress !== undefined && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-gradient-primary h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
      )}
      {status === 'success' && (
        <div className="mt-3 flex items-center space-x-2 text-xs text-success">
          <Icon name="Sparkles" size={12} color="var(--color-success)" />
          <span>Article generated successfully and ready for editing</span>
        </div>
      )}
      {status === 'error' && (
        <div className="mt-3 flex items-center space-x-2 text-xs text-error">
          <Icon name="AlertTriangle" size={12} color="var(--color-error)" />
          <span>Please check your parameters and try again</span>
        </div>
      )}
      {status === 'limit_reached' && (
        <div className="mt-3">
          <div className="flex items-center space-x-2 text-xs text-warning mb-2">
            <Icon name="Clock" size={12} color="var(--color-warning)" />
            <span>You've reached your monthly limit for news generation</span>
          </div>
          <div className="text-xs text-muted-foreground">
            Your usage will reset in 30 days, or upgrade to Pro for unlimited access
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerationStatusCard;